import React, {useEffect} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {getIsRtlLanguage, getLanguageCode} from 'modules/meta/selectors'

import {NoStyleButton} from '@vio/atlas/src/components/designSystem/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {
  Popover,
  PopoverPlacement
} from '@vio/atlas/src/components/designSystem/Popover'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/src/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {
  useGetBusinessUnitQuery,
  useGetReviewsQuery
} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {TrustpilotWidget} from '@vio/shared/src/search/TrustpilotWidget'

import {TRUSTPILOT_LABELS} from '../constants'
import {TrustpilotPopoverContent} from '../TrustpilotPopoverContent'

const BAR_HEIGHT = 45

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  color: ${cssTheme.colors.content.brand.primary};

  .--chevron-icon {
    transition: transform 0.2s ease;
    &.--chevron-icon-up {
      transform: rotate(-180deg);
      [dir='rtl'] & {
        transform: rotate(180deg);
      }
    }
  }
`

const Wrapper = styled(NoStyleButton)`
  height: ${BAR_HEIGHT}px;
  width: 100%;
  background-color: ${cssTheme.colors.background.brand.tertiary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s600};
  &:hover {
    ${ActionContainer} {
      color: ${cssTheme.colors.button.primary.hover.background};
    }
  }
  .--trustpilot-logo {
    height: auto;
    width: 74px;
    margin: 0 ${cssTheme.layout.spacing.s200};
    margin-top: -3px;
  }
`

const TrustpilotPopover = styled(Popover)`
  transform: translate3d(-20px, 40px, 0px) !important;
  [dir='rtl'] & {
    transform: translate3d(20px, 40px, 0px) !important;
  }
`

export const TrustpilotBar = () => {
  const {formatMessage} = useIntl()
  const dispatch = useDispatch()

  const {shell} = useBrandCapabilities()
  const isRtl = useSelector(getIsRtlLanguage)

  const {data, isLoading} = useGetBusinessUnitQuery(undefined, {
    skip: !shell.hasTrustpilot
  })

  const languageCode = useSelector(getLanguageCode)

  const isFeatureDisabled = !data?.name || !shell?.hasTrustpilot

  useGetReviewsQuery(
    {
      language: languageCode
    },
    {
      skip: isFeatureDisabled
    }
  )

  const actionRef = React.useRef<HTMLButtonElement>()
  const [isOpen, setIsOpen] = React.useState(false)

  const handleOpenClick = () => {
    setIsOpen(prev => !prev)
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.TrustpilotBanner,
        action: Action.Clicked
      })
    )
  }

  const handleCloseClick = React.useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const handleOutsideClick = React.useCallback(
    (e: MouseEvent) => {
      // Close the popover on any click outside the trigger button.
      if (actionRef.current && !actionRef.current.contains(e.target as Node)) {
        handleCloseClick()
      }
    },
    [actionRef, handleCloseClick]
  )

  useEffect(() => {
    if (!isOpen) return

    const handleScrollThrottled = () => {
      if (window?.scrollY > BAR_HEIGHT) handleCloseClick()
    }
    window?.addEventListener('scroll', handleScrollThrottled)
    return () => {
      window.removeEventListener('scroll', handleScrollThrottled)
    }
  }, [isOpen, handleCloseClick])

  if (isFeatureDisabled || (!isLoading && !data?.name)) return null

  return (
    <>
      <Wrapper onClick={handleOpenClick} ref={actionRef}>
        <TrustpilotWidget
          isLoading={isLoading}
          variant="banner"
          numberOfReviews={data?.numberOfReviews?.total}
          stars={data?.score?.stars}
          trustScore={data?.score?.trustScore}
        />
        <ActionContainer>
          <Text variant="labelS">
            {formatMessage(TRUSTPILOT_LABELS.seeWhatTravelersSay)}
          </Text>
          <Icon
            className={cx('--chevron-icon', isOpen && '--chevron-icon-up')}
            name="ChevronDown"
            size="sm"
          />
        </ActionContainer>
      </Wrapper>
      <TrustpilotPopover
        dataId="popover-trustpilot"
        className="--popover-trustpilot"
        isOpen={isOpen}
        placement={
          isRtl ? PopoverPlacement.BottomLeft : PopoverPlacement.BottomRight
        }
        anchorRef={actionRef}
        verticalOffset={16}
        onOutsideClick={handleOutsideClick}
      >
        <TrustpilotPopoverContent />
      </TrustpilotPopover>
    </>
  )
}
